// Pagina di ritorno dopo un pagamento Stripe riuscito.
// NON marca l'ordine come pagato: la conferma avviene server-side (webhook Stripe).
// La pagina fa polling dell'ordine: appena il webhook lo segna PAID, aggiorna la UI.
import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSiteConfig } from "../config/SiteConfigContext";
import { apiFetch } from "../api/client";
import { ga4 } from "../analytics/ga4";
import { pathFor } from "../app/paths";

type OrderStatus = "PENDING" | "PAID" | "SHIPPED" | "DELIVERED" | "CANCELLED";

interface OrderItem {
  product_id: string | null;
  product_title: string;
  quantity: number;
  price_cents: number;
}

interface Order {
  id: string;
  order_number: number | null;
  status: OrderStatus;
  payment_status: string | null;
  total_cents: number;            // LORDO (IVA inclusa)
  currency: string;
  // Ripartizione IVA (null sugli ordini pre-IVA)
  taxable_cents?: number | null;
  vat_rate?: number | null;
  vat_cents?: number | null;
  discount_code?: string | null;
  items: OrderItem[];
}

const POLL_INTERVAL_MS = 1500;
const POLL_TIMEOUT_MS = 20000;

function formatMoney(cents: number, currency: string): string {
  return new Intl.NumberFormat("it-IT", { style: "currency", currency }).format(cents / 100);
}

export function CheckoutSuccess() {
  const { locale } = useParams();
  const { t } = useTranslation();
  const cfg = useSiteConfig();
  const [params] = useSearchParams();
  const orderId = params.get("order_id");

  const [order, setOrder] = useState<Order | null>(null);
  const [pollingTimedOut, setPollingTimedOut] = useState(false);
  const noOrderId = !orderId;
  // `mode=request`: il pagamento online è in pausa e l'ordine è una richiesta
  // che un consulente finalizzerà. Non c'è nessun pagamento in arrivo, quindi
  // non c'è niente da attendere: una sola lettura, nessun polling, nessun
  // timeout. Senza il parametro la pagina si comporta esattamente come prima.
  const isRequest = params.get("mode") === "request";

  useEffect(() => {
    if (!orderId) return;
    let cancelled = false;
    const startedAt = Date.now();

    if (isRequest) {
      apiFetch<Order>(`/api/v1/shop/orders/${orderId}`)
        .then((o) => { if (!cancelled) setOrder(o); })
        .catch(() => { /* il riepilogo è un di più: la conferma è già a schermo */ });
      return () => { cancelled = true; };
    }

    const tick = async () => {
      if (cancelled) return;
      try {
        const o = await apiFetch<Order>(`/api/v1/shop/orders/${orderId}`);
        if (cancelled) return;
        setOrder(o);
        if (o.status === "PAID") return; // stop polling
      } catch { /* ignora errore transitorio, riprova */ }
      if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
        if (!cancelled) setPollingTimedOut(true);
        return;
      }
      setTimeout(tick, POLL_INTERVAL_MS);
    };
    tick();

    return () => { cancelled = true; };
  }, [orderId, isRequest]);

  // GA4 purchase: una sola volta per ordine pagato. Lo stato PAID arriva dal
  // backend (polling qui sopra), non dai parametri in URL, che sono falsificabili.
  // Guard in localStorage e non sessionStorage: deve reggere anche la riapertura
  // dell'URL in una scheda nuova, non solo i tick del polling e il refresh.
  useEffect(() => {
    // In modalità richiesta non c'è alcun acquisto: nessun `purchase` a GA4.
    // (Il controllo su PAID basterebbe, l'ordine resta PENDING — ma un evento
    // di fatturato non deve dipendere da una sola condizione implicita.)
    if (isRequest) return;
    if (!order || order.status !== "PAID") return;
    const txn = order.order_number != null ? String(order.order_number) : order.id;
    const key = `gb_ga4_purchase_${txn}`;
    try {
      if (localStorage.getItem(key)) return;
      localStorage.setItem(key, "1");
    } catch { /* storage non disponibile: emette comunque una volta per sessione JS */ }
    ga4.purchase({
      transaction_id: txn,
      valueCents: order.total_cents,   // LORDO: coincide con l'importo addebitato da Stripe
      currency: order.currency || "EUR",
      taxCents: order.vat_cents ?? null,   // valore dell'ordine, non ricalcolato
      coupon: order.discount_code ?? null,
      items: (order.items || []).map((it) => ({
        item_id: it.product_id || it.product_title,
        item_name: it.product_title,
        price: (it.price_cents || 0) / 100,
        quantity: it.quantity,
      })),
    });
  }, [order, isRequest]);

  const confirmed = order?.status === "PAID";
  const totalItems = order?.items.reduce((s, i) => s + i.quantity, 0) ?? 0;
  // Numero ordine progressivo (nuovi ordini) o codice legacy (UUID) per i vecchi.
  const shortId = order
    ? `#${order.order_number != null ? order.order_number : order.id.slice(0, 8).toUpperCase()}`
    : "";

  // Stato/testo: niente spinner infinito. In modalità richiesta lo stato è già
  // definitivo all'arrivo (la richiesta è registrata), quindi ha la precedenza
  // su tutto il resto. Altrimenti: manca order_id → stato terminale; PAID →
  // confermato; timeout → in finalizzazione; altrimenti → in conferma.
  const bodyText = isRequest
    ? t("checkout.success.bodyRequest")
    : confirmed
      ? t("checkout.success.bodyConfirmed")
      : noOrderId
        ? t("checkout.success.bodyNoOrder")
        : pollingTimedOut
          ? t("checkout.success.bodyTimeout")
          : t("checkout.success.bodyPending");

  // spinner solo mentre stiamo davvero attendendo la conferma (order_id presente, non timeout)
  const showSpinner = !isRequest && !confirmed && !noOrderId && !pollingTimedOut;

  return (
    <div style={{ minHeight: "calc(100vh - 200px)", background: cfg.color_light_bg, padding: "64px 24px" }}>
      <div
        style={{
          maxWidth: 520,
          margin: "0 auto",
          background: "#fff",
          borderRadius: 16,
          padding: "56px 40px 40px",
          boxShadow: "0 8px 32px rgba(0,0,0,0.06)",
          textAlign: "center",
        }}
      >
        {/* La richiesta È stata ricevuta con successo: segno di spunta pieno,
            anche se l'ordine resta PENDING in attesa del consulente. */}
        <SuccessMark confirmed={confirmed || isRequest} showSpinner={showSpinner} color={cfg.color_primary} />

        <h1
          style={{
            fontFamily: cfg.font_heading,
            fontSize: 32,
            color: cfg.color_dark,
            margin: "28px 0 12px",
            fontWeight: 500,
            letterSpacing: "-0.01em",
          }}
        >
          {isRequest
            ? t("checkout.success.titleRequest")
            : confirmed
              ? t("checkout.success.titleConfirmed")
              : t("checkout.success.titlePending")}
        </h1>

        <p style={{ color: cfg.color_muted, fontSize: 15, lineHeight: 1.6, margin: "0 0 32px" }}>
          {bodyText}
        </p>

        {order && (
          <div
            style={{
              borderTop: `1px solid ${cfg.color_light_bg}`,
              borderBottom: `1px solid ${cfg.color_light_bg}`,
              padding: "20px 0",
              margin: "0 0 32px",
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 16,
              textAlign: "left",
            }}
          >
            <SummaryItem label={t("checkout.success.order")} value={shortId} cfg={cfg} />
            <SummaryItem
              label={t("checkout.success.items")}
              value={`${totalItems} ${totalItems === 1 ? t("checkout.success.unit") : t("checkout.success.unitPlural")}`}
              cfg={cfg}
            />
            {order.vat_cents != null && order.vat_rate ? (
              <>
                <SummaryItem
                  label={t("cart.taxableBase")}
                  value={formatMoney(order.taxable_cents ?? 0, order.currency)}
                  cfg={cfg}
                />
                <SummaryItem
                  label={t("cart.vat", { rate: order.vat_rate })}
                  value={formatMoney(order.vat_cents, order.currency)}
                  cfg={cfg}
                />
                <SummaryItem
                  label={t("cart.totalInclVat")}
                  value={formatMoney(order.total_cents, order.currency)}
                  cfg={cfg}
                  emphasize
                />
              </>
            ) : (
              <SummaryItem
                label={t("checkout.success.totalLabel")}
                value={formatMoney(order.total_cents, order.currency)}
                cfg={cfg}
                emphasize
              />
            )}
            <SummaryItem
              label={t("checkout.success.statusLabel")}
              value={isRequest
                ? t("checkout.success.statusRequest")
                : confirmed
                  ? t("checkout.success.statusConfirmed")
                  : t("checkout.success.statusPending")}
              cfg={cfg}
              statusColor={confirmed || isRequest ? cfg.color_primary : cfg.color_accent}
            />
          </div>
        )}

        <Link
          to={`/${locale || "it"}/account`}
          style={{
            display: "inline-block",
            background: cfg.color_dark,
            color: "#fff",
            padding: "14px 36px",
            borderRadius: 999,
            textDecoration: "none",
            fontSize: 14,
            fontWeight: 500,
            letterSpacing: "0.02em",
          }}
        >
          {t("checkout.success.ctaOrders")}
        </Link>

        <div style={{ marginTop: 20 }}>
          <Link
            to={pathFor("collections", locale)}
            style={{ color: cfg.color_muted, fontSize: 13, textDecoration: "none" }}
          >
            ← {t("checkout.success.ctaShop")}
          </Link>
        </div>
      </div>
    </div>
  );
}

function SummaryItem({
  label,
  value,
  cfg,
  emphasize,
  statusColor,
}: {
  label: string;
  value: string;
  cfg: ReturnType<typeof useSiteConfig>;
  emphasize?: boolean;
  statusColor?: string;
}) {
  return (
    <div>
      <div
        style={{
          color: cfg.color_muted,
          fontSize: 11,
          textTransform: "uppercase",
          letterSpacing: "0.08em",
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      <div
        style={{
          color: statusColor || cfg.color_dark,
          fontSize: emphasize ? 20 : 15,
          fontWeight: emphasize ? 600 : 500,
          fontFamily: emphasize ? cfg.font_heading : "inherit",
        }}
      >
        {value}
      </div>
    </div>
  );
}

function SuccessMark({ confirmed, showSpinner, color }: { confirmed: boolean; showSpinner: boolean; color: string }) {
  const size = 72;
  if (showSpinner) {
    return (
      <div
        style={{
          width: size,
          height: size,
          margin: "0 auto",
          borderRadius: "50%",
          border: `3px solid ${color}33`,
          borderTopColor: color,
          animation: "gb-spin 0.9s linear infinite",
        }}
      >
        <style>{`@keyframes gb-spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }
  return (
    <svg width={size} height={size} viewBox="0 0 72 72" style={{ display: "block", margin: "0 auto" }}>
      <circle cx="36" cy="36" r="34" fill={color} opacity={confirmed ? "0.12" : "0.06"} />
      <circle cx="36" cy="36" r="26" fill={confirmed ? color : `${color}66`} />
      <path
        d="M24 36.5 L33 45.5 L49 28.5"
        stroke="#fff"
        strokeWidth="3.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  );
}
